import { supabase } from "@/integrations/supabase/client";
import type { MappedConversation } from "@/lib/conversation-list-map";
import { assignLeadsToMemberInBatches } from "@/lib/bulk-assign-leads";
import {
  BULK_BATCH_SIZE,
  bulkProgressPercent,
  chunkArray,
  type BulkOperationProgress,
} from "@/lib/bulk-lead-operations";

export type { BulkOperationProgress };
export { bulkProgressPercent, chunkArray, BULK_BATCH_SIZE };

export type BulkOperationResult = {
  succeeded: number;
  failed: number;
  errors: string[];
};

export type ScheduledMessagePayload = {
  body: string;
  scheduled_at: string;
  source_kind: string;
  source_message_id: string | null;
};

export function uniqueLeadIdsFromConversations(
  conversations: MappedConversation[],
): string[] {
  return [...new Set(conversations.map((c) => c.lead_id).filter(Boolean))];
}

export async function bulkCloseConversations(
  conversationIds: string[],
  options: {
    preserveAssignees?: boolean;
    memberId?: string | null;
    workspaceId?: string | null;
    memberDisplayName?: string | null;
    onProgress?: (progress: BulkOperationProgress) => void;
  } = {},
): Promise<BulkOperationResult> {
  const result: BulkOperationResult = { succeeded: 0, failed: 0, errors: [] };
  const total = conversationIds.length;
  let completed = 0;

  for (const conversationId of conversationIds) {
    options.onProgress?.({
      completed,
      total,
      label: "Encerrando conversas",
    });

    try {
      const { error } = await supabase
        .from("conversations")
        .update({
          is_closed: true,
          closed_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        } as Record<string, unknown>)
        .eq("id", conversationId);

      if (error) throw error;

      if (!options.preserveAssignees) {
        const { data: convRow } = await supabase
          .from("conversations")
          .select("lead_id")
          .eq("id", conversationId)
          .maybeSingle();

        if (convRow?.lead_id) {
          await supabase.rpc("remove_all_lead_assignees", {
            p_lead_id: convRow.lead_id,
          });
        }
      }

      if (options.memberId && options.workspaceId) {
        await supabase.from("member_activity_log").insert({
          workspace_id: options.workspaceId,
          member_id: options.memberId,
          conversation_id: conversationId,
          action_type: "conversation_closed",
          metadata: {
            member_name: options.memberDisplayName ?? "Membro",
            preserve_assignees: options.preserveAssignees ?? false,
            bulk: true,
          },
        });
      }

      result.succeeded += 1;
    } catch (err) {
      result.failed += 1;
      const message = err instanceof Error ? err.message : "Erro desconhecido";
      result.errors.push(`${conversationId}: ${message}`);
    }

    completed += 1;
    options.onProgress?.({
      completed,
      total,
      label: "Encerrando conversas",
    });
  }

  return result;
}

export async function bulkDeleteConversations(
  conversationIds: string[],
  onProgress?: (progress: BulkOperationProgress) => void,
): Promise<BulkOperationResult> {
  const result: BulkOperationResult = { succeeded: 0, failed: 0, errors: [] };
  const batches = chunkArray(conversationIds, 50);
  const total = conversationIds.length;
  let completed = 0;

  for (const batch of batches) {
    for (const conversationId of batch) {
      try {
        const { error: msgError } = await supabase
          .from("messages")
          .delete()
          .eq("conversation_id", conversationId);
        if (msgError) throw msgError;

        const { error: convError } = await supabase
          .from("conversations")
          .delete()
          .eq("id", conversationId);
        if (convError) throw convError;

        result.succeeded += 1;
      } catch (err) {
        result.failed += 1;
        const message = err instanceof Error ? err.message : "Erro desconhecido";
        result.errors.push(`${conversationId}: ${message}`);
      }

      completed += 1;
      onProgress?.({
        completed,
        total,
        label: "Excluindo conversas",
      });
    }
  }

  return result;
}

export async function bulkAssignLeadsToMember(
  leadIds: string[],
  memberId: string,
  options?: {
    workspaceId?: string;
    onProgress?: (progress: BulkOperationProgress) => void;
  },
): Promise<BulkOperationResult> {
  if (!options?.workspaceId) {
    throw new Error("Workspace não definido.");
  }

  const counts = await assignLeadsToMemberInBatches(
    options.workspaceId,
    leadIds,
    memberId,
    options.onProgress,
  );

  return {
    succeeded: counts.assigned,
    failed: counts.skipped,
    errors:
      counts.skipped > 0
        ? [`${counts.skipped} lead(s) sem permissão para atribuir.`]
        : [],
  };
}

export async function bulkAddTagToLeads(
  leadIds: string[],
  tagId: string,
  onProgress?: (progress: BulkOperationProgress) => void,
): Promise<BulkOperationResult> {
  const result: BulkOperationResult = { succeeded: 0, failed: 0, errors: [] };
  const inserts = leadIds.map((leadId) => ({ lead_id: leadId, tag_id: tagId }));
  const batches = chunkArray(inserts, BULK_BATCH_SIZE);
  const total = leadIds.length;
  let completed = 0;

  for (const batch of batches) {
    try {
      const { error } = await supabase
        .from("lead_tag_assignments")
        .upsert(batch, { onConflict: "lead_id,tag_id", ignoreDuplicates: true });
      if (error) throw error;
      result.succeeded += batch.length;
    } catch (err) {
      result.failed += batch.length;
      const message = err instanceof Error ? err.message : "Erro desconhecido";
      result.errors.push(message);
    }

    completed += batch.length;
    onProgress?.({
      completed: Math.min(completed, total),
      total,
      label: "Adicionando tag",
    });
  }

  return result;
}

export async function bulkRemoveTagFromLeads(
  leadIds: string[],
  tagId: string,
  onProgress?: (progress: BulkOperationProgress) => void,
): Promise<BulkOperationResult> {
  const result: BulkOperationResult = { succeeded: 0, failed: 0, errors: [] };
  const batches = chunkArray(leadIds, BULK_BATCH_SIZE);
  const total = leadIds.length;
  let completed = 0;

  for (const batch of batches) {
    try {
      const { error } = await supabase
        .from("lead_tag_assignments")
        .delete()
        .in("lead_id", batch)
        .eq("tag_id", tagId);
      if (error) throw error;
      result.succeeded += batch.length;
    } catch (err) {
      result.failed += batch.length;
      const message = err instanceof Error ? err.message : "Erro desconhecido";
      result.errors.push(message);
    }

    completed += batch.length;
    onProgress?.({
      completed: Math.min(completed, total),
      total,
      label: "Removendo tag",
    });
  }

  return result;
}

export async function bulkMoveLeadsToStage(
  leadIds: string[],
  workspaceId: string,
  stageSlug: string,
  options?: {
    funnelId?: string | null;
    sourceFunnelId?: string | null;
    targetFunnelId?: string | null;
    onProgress?: (progress: BulkOperationProgress) => void;
  },
): Promise<BulkOperationResult> {
  const result: BulkOperationResult = { succeeded: 0, failed: 0, errors: [] };
  const batches = chunkArray(leadIds, BULK_BATCH_SIZE);
  const total = leadIds.length;
  let completed = 0;

  const isCrossFunnel =
    Boolean(
      options?.targetFunnelId &&
      options?.sourceFunnelId &&
      options.targetFunnelId !== options.sourceFunnelId,
    );

  for (const batch of batches) {
    try {
      if (isCrossFunnel) {
        const { error } = await supabase.rpc("bulk_move_leads_to_funnel", {
          p_workspace_id: workspaceId,
          p_source_funnel_id: options!.sourceFunnelId!,
          p_target_funnel_id: options!.targetFunnelId!,
          p_target_stage: stageSlug,
          p_lead_ids: batch,
        });
        if (error) throw error;
      } else {
        const { error } = await supabase.rpc("bulk_move_leads_to_stage", {
          p_workspace_id: workspaceId,
          p_funnel_id: options?.funnelId ?? null,
          p_stage: stageSlug,
          p_lead_ids: batch,
        });
        if (error) throw error;
      }
      result.succeeded += batch.length;
    } catch (err) {
      result.failed += batch.length;
      const message = err instanceof Error ? err.message : "Erro desconhecido";
      result.errors.push(message);
    }

    completed += batch.length;
    options?.onProgress?.({
      completed: Math.min(completed, total),
      total,
      label: "Movendo leads",
    });
  }

  return result;
}

const SCHEDULE_ERROR_MAP: Record<string, string> = {
  conversation_not_found: "Conversa não encontrada.",
  workspace_mismatch: "Sem permissão para esta conversa.",
  scheduled_messages_whatsapp_only: "Agendamento disponível apenas para WhatsApp.",
  conversation_has_no_lead: "Conversa sem lead associado.",
  no_active_whatsapp_integration: "Nenhuma integração WhatsApp ativa.",
  too_many_messages: "Máximo de 5 mensagens por agendamento.",
  no_messages_provided: "Adicione ao menos uma mensagem.",
  message_body_required: "Existe uma mensagem sem conteúdo.",
  scheduled_at_must_be_future: "Todos os agendamentos devem ser no futuro.",
};

export async function bulkScheduleMessages(
  conversations: MappedConversation[],
  payload: ScheduledMessagePayload[],
  closeConversation: boolean,
  onProgress?: (progress: BulkOperationProgress) => void,
): Promise<BulkOperationResult & { skippedNonWhatsapp: number }> {
  const result: BulkOperationResult & { skippedNonWhatsapp: number } = {
    succeeded: 0,
    failed: 0,
    errors: [],
    skippedNonWhatsapp: 0,
  };

  const eligible = conversations.filter((c) => c.channel === "whatsapp");
  result.skippedNonWhatsapp = conversations.length - eligible.length;

  const total = eligible.length;
  let completed = 0;

  for (const conv of eligible) {
    onProgress?.({
      completed,
      total,
      label: closeConversation ? "Agendando e encerrando" : "Agendando mensagens",
    });

    try {
      const { error } = await supabase.rpc("schedule_messages_batch", {
        _conversation_id: conv.conversation_id,
        _messages: payload as unknown as never,
        _close_conversation: closeConversation,
      });

      if (error) {
        const msg = SCHEDULE_ERROR_MAP[error.message] || error.message;
        throw new Error(msg);
      }

      result.succeeded += 1;
    } catch (err) {
      result.failed += 1;
      const message = err instanceof Error ? err.message : "Erro desconhecido";
      result.errors.push(`${conv.lead_name}: ${message}`);
    }

    completed += 1;
    onProgress?.({
      completed,
      total,
      label: closeConversation ? "Agendando e encerrando" : "Agendando mensagens",
    });
  }

  return result;
}

export function summarizeBulkResult(
  result: BulkOperationResult,
  actionLabel: string,
): { title: string; description: string; variant?: "destructive" } {
  if (result.failed === 0) {
    return {
      title: `${actionLabel} concluído`,
      description: `${result.succeeded} conversa(s) processada(s) com sucesso.`,
    };
  }

  if (result.succeeded === 0) {
    return {
      title: `Falha ao ${actionLabel.toLowerCase()}`,
      description: result.errors[0] || "Nenhuma conversa foi processada.",
      variant: "destructive",
    };
  }

  return {
    title: `${actionLabel} parcial`,
    description: `${result.succeeded} concluída(s), ${result.failed} com erro.`,
  };
}
